import { useState, type FormEvent } from 'react';
import { updatePassword } from 'aws-amplify/auth';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Field, Notice } from '../components/ui';
import { Dialog } from '../components/Dialog';
import { PasswordStrength } from '../components/PasswordStrength';
import { api, ApiError } from '../lib/api';
import { authErrorMessage, passwordChecks } from '../lib/auth';
import { useMe, useSession } from '../lib/session';
import '../components/Form.css';
import './Page.css';

export function AccountPage() {
  const me = useMe();
  const { refresh, signOut } = useSession();
  const navigate = useNavigate();

  // Deleting: check first, so an only owner is asked to hand over before anything happens.
  type Check = { allowed: boolean; reason: 'last_owner' | null; closesSchool: boolean; schoolName: string | null };
  const [check, setCheck] = useState<Check | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Type the organisation's name (or DELETE, with no organisation) to confirm (S-04).
  const [confirmText, setConfirmText] = useState('');
  const confirmWord = check?.schoolName ?? 'DELETE';
  const confirmed = confirmText.trim().toLowerCase() === confirmWord.trim().toLowerCase();

  const startDelete = async () => {
    setDeleteError(null);
    setDeleteBusy(true);
    try {
      setCheck(await api<Check>('/users/me/deletion-check'));
      setConfirmText('');
      setDeleteOpen(true);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : 'That didn’t work. Try again.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const confirmDelete = async () => {
    setDeleteError(null);
    setDeleteBusy(true);
    try {
      await api('/users/me', { method: 'DELETE' });
      try {
        await signOut();
      } catch {
        // The sign-in account is already gone; nothing left to sign out of.
      }
      navigate('/sign-in', { replace: true });
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : 'That didn’t work. Try again.');
      setDeleteBusy(false);
    }
  };
  const [name, setName] = useState(me.name);
  const [jobTitle, setJobTitle] = useState(me.job_title ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMessage, setPwMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    if (!name.trim()) {
      setError('Your name can’t be empty.');
      return;
    }
    setSaving(true);
    try {
      await api('/users/me', { method: 'PUT', body: JSON.stringify({ name: name.trim(), jobTitle }) });
      await refresh();
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn’t save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPwMessage(null);
    if (!passwordChecks(next).every((c) => c.ok)) {
      setPwMessage({ tone: 'error', text: 'Choose a new password that meets all the rules.' });
      return;
    }
    setPwBusy(true);
    try {
      await updatePassword({ oldPassword: current, newPassword: next });
      setCurrent('');
      setNext('');
      setPwMessage({ tone: 'success', text: 'Password changed.' });
    } catch (err) {
      setPwMessage({ tone: 'error', text: authErrorMessage(err) });
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="page">
      <header className="page-head">
        <h1>Your account</h1>
        <p className="muted">The same account signs you in to the EducAitor app and the web.</p>
      </header>

      <section className="panel form-section">
        <h2>Profile</h2>
        <form className="form-grid" onSubmit={saveProfile} noValidate>
          {error ? <Notice tone="error">{error}</Notice> : null}
          <div className="readonly">
            <span className="readonly-label">Email</span>
            <span className="readonly-value">{me.email}</span>
          </div>
          <Field id="account-name" label="Name" value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} autoComplete="name" />
          <Field id="account-title" label="Job title" value={jobTitle} onChange={(e) => { setJobTitle(e.target.value); setSaved(false); }} placeholder="e.g. Deputy headteacher" />
          <div className="form-actions">
            <Button type="submit" loading={saving}>
              Save
            </Button>
            {saved ? <span className="form-saved">Saved</span> : null}
          </div>
        </form>
      </section>

      <section className="panel form-section">
        <div>
          <h2>Password</h2>
          <p className="muted">If you sign in with Google or Apple, you manage your password with them instead.</p>
        </div>
        <form className="form-grid" onSubmit={changePassword} noValidate>
          {pwMessage ? <Notice tone={pwMessage.tone}>{pwMessage.text}</Notice> : null}
          <Field id="account-current" label="Current password" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
          <Field id="account-new" label="New password" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
          <PasswordStrength password={next} />
          <div className="form-actions">
            <Button type="submit" variant="secondary" loading={pwBusy} disabled={!current || !next}>
              Change password
            </Button>
          </div>
        </form>
      </section>

      <section className="panel form-section">
        <div>
          <h2>Delete account</h2>
          <p className="muted">
            Removes your account, your lessons and your reports from EducAitor, on the web and in the app. This can&rsquo;t be undone.
          </p>
        </div>
        {deleteError && !deleteOpen ? <Notice tone="error">{deleteError}</Notice> : null}
        <div className="form-actions">
          <Button variant="danger" onClick={startDelete} loading={deleteBusy && !deleteOpen}>
            Delete account
          </Button>
        </div>
      </section>

      <Dialog
        open={deleteOpen}
        onClose={() => !deleteBusy && setDeleteOpen(false)}
        title={check?.allowed === false ? 'Hand over first' : 'Delete your account?'}
      >
        {check?.allowed === false ? (
          <>
            <p>
              You&rsquo;re the only owner of {check.schoolName ?? 'your organisation'}. Transfer ownership first: make someone else an owner from People, then you
              can delete your account.
            </p>
            <div className="dialog-actions">
              <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
                Cancel
              </Button>
              <Link className="btn btn-primary btn-md" to="/people" onClick={() => setDeleteOpen(false)}>
                Go to People
              </Link>
            </div>
          </>
        ) : (
          <>
            <p>
              Your account, lessons and reports will be permanently deleted.
              {check?.closesSchool
                ? ` No one else is in ${check.schoolName ?? 'your organisation'}, so the organisation will be closed too.`
                : ''}
            </p>
            <Field
              id="delete-confirm"
              label={`Type ${confirmWord} to confirm`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
            />
            {deleteError ? <Notice tone="error">{deleteError}</Notice> : null}
            <div className="dialog-actions">
              <Button variant="ghost" onClick={() => setDeleteOpen(false)} disabled={deleteBusy}>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmDelete} loading={deleteBusy} disabled={!confirmed}>
                Delete account
              </Button>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
}
